import { api } from '../slices/api';

/* ================================
   Types
================================ */

export interface IBuildingRef {
	id: string;
	name: string;
	slug?: string;
}

/** Lean location used to draw the rack diagram. */
export interface ILocationDiagram {
	id: string;
	code: string;
	type: string;
	description?: string | null;
	max_volume: number | null;
	max_width: number | null;
	max_height: number | null;
	occupied_volume: number;
	occupied_width: number;
	occupied_height: number;
	occupied_weight: number;
	number_of_items: number;
	occupancy_percent: number;
}

export interface IShelfWithLocations {
	id: string;
	name: string;
	max_weight: number | null;
	material_type?: string | null;
	shape_category?: string | null;
	production_method_type?: string | null;
	description?: string | null;
	reservations_enabled?: boolean;
	occupied_weight: number;
	locations: ILocationDiagram[];
}

/** Rack summary for the master panel. */
export interface IRackListItem {
	id: string;
	rack_code: string;
	rack_name: string;
	storage_type: string;
	min_size: number | null;
	max_size: number | null;
	description?: string | null;
	reservations_enabled?: boolean;
	building: IBuildingRef;
	shelves_count: number;
	locations_count: number;
	total_items: number;
}

/** Full rack with nested shelves and locations. */
export interface IRackDetail {
	id: string;
	rack_code: string;
	rack_name: string;
	storage_type: string;
	min_size: number | null;
	max_size: number | null;
	description?: string | null;
	reservations_enabled?: boolean;
	building: IBuildingRef;
	shelves: IShelfWithLocations[];
}

export interface IRackWrite {
	rack_code: string;
	rack_name: string;
	building: string;
	storage_type: string;
	min_size?: number | null;
	max_size?: number | null;
	description?: string | null;
	reservations_enabled?: boolean;
}

export interface IShelfWrite {
	rack: string;
	name: string;
	max_weight?: number | null;
	material_type?: string | null;
	shape_category?: string | null;
	production_method_type?: string | null;
	description?: string | null;
	reservations_enabled?: boolean;
}

export interface ILocationWrite {
	shelf: string;
	type: string;
	code: string;
	description?: string | null;
	max_volume?: number | null;
	max_width?: number | null;
	max_height?: number | null;
}

/** A material length (bar) stored inside a location. */
export interface IMaterialLengthRow {
	id: string;
	friendly_id: string;
	weight: number | string | null;
	current_length: number | string | null;
	bundle_id: string | null;
	material_type: string | null;
	material_shape: string | null;
	shape_id: string | null;
}

export interface IPaginatedBars {
	count: number;
	next: string | null;
	previous: string | null;
	results: IMaterialLengthRow[];
}

/** Result of locating a material (bar) by its friendly_id. */
export interface ILocateMaterialResult {
	friendly_id: string;
	material_length_id: string;
	current_length: number | string | null;
	material_type: string | null;
	/** True when the material is assigned to an inventory location. */
	located: boolean;
	building: IBuildingRef | null;
	rack: { id: string; rack_code: string; rack_name: string } | null;
	shelf: { id: string; name: string } | null;
	location: { id: string; code: string; display_name: string } | null;
}

export interface IGenerateStructurePayload {
	rack: IRackWrite;
	num_shelves: number;
	locations_per_shelf: number;
	defaults?: {
		max_weight?: number | null;
		max_volume?: number | null;
		max_width?: number | null;
		max_height?: number | null;
	};
}

/** The slot itself (dimensions the front view is drawn against). */
export interface ISlotLayoutSlot {
	id: string;
	code: string;
	max_width: number;
	max_height: number;
	clearance: number;
}

/**
 * A single material length's 2D footprint within the slot's front view.
 * `x`/`y`/`w`/`h` are in inches within the slot's `max_width` x `max_height`
 * (origin top-left). They are null when the packer couldn't place the bar.
 */
export interface ISlotLayoutPlacement {
	material_length_id: string;
	friendly_id: string;
	shape: string;
	material_type: string;
	x: number | null;
	y: number | null;
	w: number | null;
	h: number | null;
	rotated: boolean;
	weight: number;
}

/** A suggestion for how many more bars of a given footprint would still fit. */
export interface ISlotLayoutFitsMore {
	w: number;
	h: number;
	count: number;
}

/** Response of GET /inventory/locations/{id}/slot-layout/ (INDIVIDUAL_SLOTS only). */
export interface ISlotLayout {
	slot: ISlotLayoutSlot;
	placements: ISlotLayoutPlacement[];
	occupied: { bars: number; weight: number };
	fits_more: ISlotLayoutFitsMore[];
	all_placed: boolean;
}

/** Alias matching the backend contract's own name for this shape. */
export type SlotLayout = ISlotLayout;

/* ================================
   Endpoints
================================ */

const INVALIDATE_ALL = ['Racks', 'Shelves', 'Locations'] as const;

const inventoryApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// ─── Racks ───────────────────────────────────────────────
		getRacks: builder.query<IRackListItem[], { building?: string } | void>({
			query: (params) =>
				params?.building
					? `inventory/racks/?building=${params.building}`
					: 'inventory/racks/',
			providesTags: ['Racks'],
		}),
		getRack: builder.query<IRackDetail, string>({
			query: (id) => `inventory/racks/${id}/`,
			providesTags: (_r, _e, id) => [{ type: 'Racks', id }],
		}),
		createRack: builder.mutation<IRackDetail, IRackWrite>({
			query: (body) => ({ url: 'inventory/racks/', method: 'POST', body }),
			invalidatesTags: [...INVALIDATE_ALL],
		}),
		updateRack: builder.mutation<
			IRackDetail,
			{ id: string; body: Partial<IRackWrite> }
		>({
			query: ({ id, body }) => ({
				url: `inventory/racks/${id}/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: [...INVALIDATE_ALL],
		}),
		deleteRack: builder.mutation<void, string>({
			query: (id) => ({ url: `inventory/racks/${id}/`, method: 'DELETE' }),
			invalidatesTags: [...INVALIDATE_ALL],
		}),
		recalculateRackValues: builder.mutation<{ recalculated: number }, string>({
			query: (id) => ({
				url: `inventory/racks/${id}/recalculate-values/`,
				method: 'POST',
			}),
			invalidatesTags: (_r, _e, id) => [{ type: 'Racks', id }],
		}),
		generateRackStructure: builder.mutation<IRackDetail, IGenerateStructurePayload>({
			query: (body) => ({
				url: 'inventory/racks/generate-structure/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [...INVALIDATE_ALL],
		}),

		// ─── Shelves ─────────────────────────────────────────────
		createShelf: builder.mutation<IShelfWithLocations, IShelfWrite>({
			query: (body) => ({ url: 'inventory/shelves/', method: 'POST', body }),
			invalidatesTags: [...INVALIDATE_ALL],
		}),
		updateShelf: builder.mutation<
			IShelfWithLocations,
			{ id: string; body: Partial<IShelfWrite> }
		>({
			query: ({ id, body }) => ({
				url: `inventory/shelves/${id}/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: [...INVALIDATE_ALL],
		}),
		deleteShelf: builder.mutation<void, string>({
			query: (id) => ({ url: `inventory/shelves/${id}/`, method: 'DELETE' }),
			invalidatesTags: [...INVALIDATE_ALL],
		}),

		// ─── Locations ───────────────────────────────────────────
		locateMaterial: builder.query<ILocateMaterialResult, string>({
			query: (friendlyId) =>
				`inventory/locations/locate/?friendly_id=${encodeURIComponent(
					friendlyId.trim(),
				)}`,
		}),
		getLocationBars: builder.query<IPaginatedBars, { id: string; page: number }>({
			query: ({ id, page }) =>
				`inventory/locations/${id}/bars/?page=${page}&page_size=20`,
			providesTags: (_r, _e, { id }) => [{ type: 'Locations', id }],
		}),
		/** Read-only 2D packing diagram for an INDIVIDUAL_SLOTS location. */
		getSlotLayout: builder.query<ISlotLayout, string>({
			query: (id) => `inventory/locations/${id}/slot-layout/`,
			providesTags: (_r, _e, id) => [{ type: 'Locations', id }],
		}),
		createLocation: builder.mutation<ILocationDiagram, ILocationWrite>({
			query: (body) => ({ url: 'inventory/locations/', method: 'POST', body }),
			invalidatesTags: [...INVALIDATE_ALL],
		}),
		updateLocation: builder.mutation<
			ILocationDiagram,
			{ id: string; body: Partial<ILocationWrite> }
		>({
			query: ({ id, body }) => ({
				url: `inventory/locations/${id}/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: [...INVALIDATE_ALL],
		}),
		deleteLocation: builder.mutation<void, string>({
			query: (id) => ({ url: `inventory/locations/${id}/`, method: 'DELETE' }),
			invalidatesTags: [...INVALIDATE_ALL],
		}),
	}),
});

export const {
	useGetRacksQuery,
	useGetRackQuery,
	useLazyLocateMaterialQuery,
	useGetLocationBarsQuery,
	useGetSlotLayoutQuery,
	useCreateRackMutation,
	useUpdateRackMutation,
	useDeleteRackMutation,
	useRecalculateRackValuesMutation,
	useGenerateRackStructureMutation,
	useCreateShelfMutation,
	useUpdateShelfMutation,
	useDeleteShelfMutation,
	useCreateLocationMutation,
	useUpdateLocationMutation,
	useDeleteLocationMutation,
} = inventoryApi;

export default inventoryApi;
